import { RefObject, useEffect, useEffectEvent, useRef, useState } from "react";
import clsx from "clsx";

type Axis = "x" | "y";

const STEP_KEYS: Record<Axis, Record<string, number>> = {
  x: { ArrowRight: 1, ArrowLeft: -1 },
  y: { ArrowDown: 1, ArrowUp: -1 },
};

interface Drag {
  grid: HTMLElement;
  startPointer: number;
  startOrigin: number;
  startSize: number;
  pointer: number;
}

type ResizeHandleProps = {
  axis: Axis;
  label: string;
  value: number;
  valueText?: string;
  min: number;
  max: number;
  box: RefObject<HTMLElement | null>;
  grid: RefObject<HTMLElement | null>;
  onDrag: (sizePx: number) => void;
  onStep: (delta: number) => void;
};

export const ResizeHandle = ({ axis, label, value, valueText, min, max, box, grid, onDrag, onStep }: ResizeHandleProps) => {
  const drag = useRef<Drag | null>(null);
  const [dragging, setDragging] = useState(false);

  const originOf = (element: HTMLElement) => {
    const rect = element.getBoundingClientRect();
    return axis === "x" ? rect.left : rect.top;
  };

  const report = () => {
    if (!drag.current) return;
    const { grid, startPointer, startOrigin, startSize, pointer } = drag.current;
    onDrag(startSize + (pointer - startPointer) - (originOf(grid) - startOrigin));
  };

  const reportScroll = useEffectEvent(report);

  useEffect(() => {
    if (!dragging) return;
    window.addEventListener("scroll", reportScroll, true);
    return () => window.removeEventListener("scroll", reportScroll, true);
  }, [dragging]);

  const stop = () => {
    drag.current = null;
    setDragging(false);
  };

  return (
    <div
      role="separator"
      tabIndex={0}
      aria-label={label}
      aria-orientation={axis === "x" ? "vertical" : "horizontal"}
      aria-valuenow={value}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuetext={valueText}
      className={clsx(
        "absolute z-10 rounded-full transition-colors hover:bg-theme-focus focus-visible:bg-theme-focus outline-none",
        axis === "x" ? "top-0 right-0 h-full w-2 cursor-ew-resize" : "bottom-0 left-0 w-full h-2 cursor-ns-resize"
      )}
      onPointerDown={e => {
        if (!box.current || !grid.current) return;
        const card = box.current.getBoundingClientRect();
        const pointer = axis === "x" ? e.clientX : e.clientY;
        drag.current = {
          grid: grid.current,
          startPointer: pointer,
          startOrigin: originOf(grid.current),
          startSize: axis === "x" ? card.width : card.height,
          pointer,
        };
        setDragging(true);
        e.currentTarget.setPointerCapture(e.pointerId);
      }}
      onPointerMove={e => {
        if (!drag.current) return;
        drag.current.pointer = axis === "x" ? e.clientX : e.clientY;
        report();
      }}
      onPointerUp={stop}
      onPointerCancel={stop}
      onKeyDown={e => {
        const delta = STEP_KEYS[axis][e.key];
        if (!delta) return;
        e.preventDefault();
        onStep(delta);
      }}
    />
  );
};
